/* eslint-disable react-refresh/only-export-components --
 * This is a config file, not a component module. The exported registry
 * happens to contain JSX render closures, which the rule conservatively
 * treats as "looks component-y". Splitting wouldn't help because the
 * non-JSX exports below derive from the registry that has JSX — the
 * dependency would just bounce between files.
 */
import {
  ClipboardCheck,
  ClipboardList,
  Dna,
  FileText,
  FlaskConical,
  GitFork,
  HeartPulse,
  MessageSquareText,
  Route,
  ShieldCheck,
  Stethoscope,
  type LucideIcon,
} from 'lucide-react';
import type { ReactNode } from 'react';

import { PedigreeSection } from '@/features/pedigree/PedigreeSection';
import type { PatientViewModel } from '@/models/patient';

import { CarePathwaysSection } from './components/CarePathwaysSection';
import { ConsentGovernanceSection } from './components/ConsentGovernanceSection';
import { DemographicsSection } from './components/DemographicsSection';
import { DiagnosesSection } from './components/DiagnosesSection';
import { DocumentsSection } from './components/DocumentsSection';
import { GeneticInfoSection } from './components/GeneticInfoSection';
import { MedicalHistorySection } from './components/MedicalHistorySection';
import { NotesSection } from './components/NotesSection';
import { PhenotypesSection } from './components/PhenotypesSection';
import { QuestionnairesSection } from './components/QuestionnairesSection';
import { TestOrdersSection } from './components/TestOrdersSection';

/**
 * Single source of truth for the patient-record sections.
 *
 * Three consumers used to declare this list independently — the JSX
 * render order in `PatientPage`, the `SECTION_IDS` array for scroll-spy,
 * and the anchor strings inside `patientSidebar`. They drifted out of
 * sync the moment a section was added or renamed; this registry collapses
 * them into one entry per section that all three derive from.
 *
 * - `id` is the DOM id, the hash anchor, and the scroll-spy key — same
 *   string everywhere by design.
 * - `group` decides which sidebar group the entry lands in.
 * - `labelKey` is the i18n key for the sidebar label, same key shape
 *   used elsewhere under `routes.patient.sidebar.*`.
 * - `render` produces the JSX for the section. Most sections take only
 *   the `guid`; Demographics receives `demographics` and `identifiers`
 *   directly because the patient query already fetched them — passing
 *   them down avoids a duplicate request.
 */

export type PatientSectionGroup = 'identity' | 'clinical' | 'care' | 'governance';

export interface PatientSubsectionDescriptor {
  /** Anchor id rendered inside the section's content (`id="..."`). */
  readonly id: string;
  readonly labelKey: string;
}

export interface PatientSectionDescriptor {
  readonly id: string;
  readonly group: PatientSectionGroup;
  readonly labelKey: string;
  readonly Icon: LucideIcon;
  readonly render: (patient: PatientViewModel) => ReactNode;
  /**
   * Sub-tab anchors rendered under the case header when this section is
   * active. Omit when the section has only one logical region — the
   * sub-tab strip hides itself for those.
   */
  readonly subsections?: readonly PatientSubsectionDescriptor[];
}

export const PATIENT_RECORD_SECTIONS: readonly PatientSectionDescriptor[] = [
  {
    id: 'demographics',
    group: 'identity',
    labelKey: 'routes.patient.sidebar.demographics',
    Icon: ClipboardList,
    render: (patient) => <DemographicsSection patient={patient} />,
    subsections: [
      { id: 'personal-info', labelKey: 'patient.demographics.personal' },
      { id: 'identifiers', labelKey: 'patient.demographics.identifiers' },
      { id: 'contact', labelKey: 'patient.demographics.contact' },
      { id: 'addresses', labelKey: 'patient.demographics.addresses' },
      { id: 'ethnicity', labelKey: 'patient.demographics.ethnicity' },
      { id: 'physical-chart', labelKey: 'patient.demographics.physicalChart' },
    ],
  },
  {
    id: 'pedigree',
    group: 'identity',
    labelKey: 'routes.patient.sidebar.pedigree',
    Icon: GitFork,
    render: (patient) => <PedigreeSection guid={patient.guid} />,
  },
  {
    id: 'medical-history',
    group: 'clinical',
    labelKey: 'routes.patient.sidebar.medicalHistory',
    Icon: HeartPulse,
    render: (patient) => (
      <MedicalHistorySection
        guid={patient.guid}
        dateOfBirth={patient.demographics.dateOfBirth}
      />
    ),
    subsections: [
      { id: 'narrative', labelKey: 'patient.medicalHistory.narrativeHeading' },
      { id: 'allergies', labelKey: 'patient.medicalHistory.allergies.heading' },
      { id: 'measurements', labelKey: 'patient.medicalHistory.measurements' },
    ],
  },
  {
    id: 'phenotypes',
    group: 'clinical',
    labelKey: 'routes.patient.sidebar.phenotypes',
    Icon: Dna,
    render: (patient) => <PhenotypesSection guid={patient.guid} />,
  },
  {
    id: 'diagnoses',
    group: 'clinical',
    labelKey: 'routes.patient.sidebar.diagnoses',
    Icon: Stethoscope,
    render: (patient) => <DiagnosesSection guid={patient.guid} />,
  },
  {
    id: 'genetic-info',
    group: 'clinical',
    labelKey: 'routes.patient.sidebar.geneticInfo',
    Icon: FlaskConical,
    render: (patient) => <GeneticInfoSection guid={patient.guid} />,
  },
  {
    id: 'notes',
    group: 'care',
    labelKey: 'routes.patient.sidebar.notes',
    Icon: MessageSquareText,
    render: (patient) => <NotesSection guid={patient.guid} />,
  },
  {
    id: 'documents',
    group: 'care',
    labelKey: 'routes.patient.sidebar.documents',
    Icon: FileText,
    render: (patient) => <DocumentsSection guid={patient.guid} />,
  },
  {
    id: 'care-pathways',
    group: 'care',
    labelKey: 'routes.patient.sidebar.carePathways',
    Icon: Route,
    render: (patient) => <CarePathwaysSection guid={patient.guid} />,
  },
  {
    id: 'test-orders',
    group: 'care',
    labelKey: 'routes.patient.sidebar.testOrders',
    Icon: FlaskConical,
    render: (patient) => <TestOrdersSection guid={patient.guid} />,
  },
  {
    id: 'questionnaires',
    group: 'care',
    labelKey: 'routes.patient.sidebar.questionnaires',
    Icon: ClipboardCheck,
    render: (patient) => <QuestionnairesSection guid={patient.guid} />,
  },
  {
    id: 'consent',
    group: 'governance',
    labelKey: 'routes.patient.sidebar.consent',
    Icon: ShieldCheck,
    render: (patient) => <ConsentGovernanceSection guid={patient.guid} />,
  },
];

/** Stable ordered list of section ids — fed straight to scroll-spy. */
export const PATIENT_RECORD_SECTION_IDS: readonly string[] = PATIENT_RECORD_SECTIONS.map(
  (section) => section.id,
);

/** Group order in the sidebar — registry items are bucketed by this list. */
export const PATIENT_SECTION_GROUP_ORDER: readonly PatientSectionGroup[] = [
  'identity',
  'clinical',
  'care',
  'governance',
];
